import { Monitor01, Moon01, Sun } from "@untitledui/icons";
import { useTranslation } from "react-i18next";
import { SegmentedControl, type SegmentedOption } from "@/components/app/SegmentedControl";
import { useThemeStore, type ThemeMode } from "@/stores/themeStore";

/**
 * ThemeToggle — přepínač vzhledu (Systém / Světlý / Tmavý) nad sdíleným
 * {@link SegmentedControl}. Řídí `useThemeStore.setMode`, který přepíná `.dark`
 * třídu na <html> a perzistuje volbu do localStorage.
 *
 * Renderuje se v menu účtu (v patičce sidebaru i pod avatarem v horní liště) a stav
 * drží globální store, takže všechny instance ukazují totéž. Rychlé přehození světlý
 * ↔ tmavý má lišta jako jednu ikonu ({@link ThemeIconToggle}).
 */
const MODES: { mode: ThemeMode; icon: typeof Sun; labelKey: string }[] = [
  { mode: "system", icon: Monitor01, labelKey: "theme.system" },
  { mode: "light", icon: Sun, labelKey: "theme.light" },
  { mode: "dark", icon: Moon01, labelKey: "theme.dark" },
];

export interface ThemeToggleProps {
  size?: "sm" | "md";
  className?: string;
}

export function ThemeToggle({ size, className }: ThemeToggleProps) {
  const { t } = useTranslation();
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  const options: SegmentedOption<ThemeMode>[] = MODES.map(({ mode: value, icon, labelKey }) => ({
    value,
    label: t(labelKey),
    icon,
  }));

  return (
    <SegmentedControl
      options={options}
      value={mode}
      onChange={setMode}
      ariaLabel={t("theme.label")}
      size={size}
      className={className}
    />
  );
}
